import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

export type UpgradePathStep = {
  order?: number | string;
  action?: string;
  details?: string;
  is_critical?: boolean;
  isCritical?: boolean;
};

export type UpgradeIssue = {
  severity?: string;
  category?: string;
  resource?: string;
  description?: string;
  remediation?: string;
};

export type EksUpgradeReport = {
  generatedAt?: string;
  readiness?: string;
  currentVersion?: string;
  targetVersion?: string;
  issueCounts?: {
    critical?: number;
    warning?: number;
    info?: number;
  };
  issues?: UpgradeIssue[];
  upgradePath?: UpgradePathStep[];
};

const useStyles = makeStyles(theme => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  order: {
    alignItems: 'center',
    background: theme.palette.grey[200],
    borderRadius: '50%',
    display: 'inline-flex',
    flex: '0 0 auto',
    fontWeight: 700,
    height: 28,
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    width: 28,
  },
  item: {
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
  },
  actionRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  details: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    wordBreak: 'break-word',
  },
  issueMeta: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    wordBreak: 'break-word',
  },
  emptyState: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 0),
  },
}));

function valueText(value: unknown, fallback = 'unknown'): string {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return String(value);
}

function count(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortedUpgradePath(steps: UpgradePathStep[]): UpgradePathStep[] {
  return [...steps].sort((a, b) => {
    const aOrder = Number(a.order);
    const bOrder = Number(b.order);
    const aHasOrder = Number.isFinite(aOrder);
    const bHasOrder = Number.isFinite(bOrder);

    if (aHasOrder && bHasOrder) {
      return aOrder - bOrder;
    }
    if (aHasOrder) {
      return -1;
    }
    if (bHasOrder) {
      return 1;
    }
    return 0;
  });
}

function issueSeverityColor(severity: string): 'default' | 'primary' | 'secondary' {
  const normalized = severity.toUpperCase();
  if (normalized === 'CRITICAL') {
    return 'secondary';
  }
  if (normalized === 'WARNING') {
    return 'primary';
  }
  return 'default';
}

type EksUpgradeReportDialogProps = {
  open: boolean;
  clusterName: string;
  report?: EksUpgradeReport;
  onClose: () => void;
};

export function EksUpgradeReportDialog({
  open,
  clusterName,
  report,
  onClose,
}: EksUpgradeReportDialogProps) {
  const classes = useStyles();
  const steps = sortedUpgradePath(report?.upgradePath ?? []);
  const issues = report?.issues ?? [];
  const issueCounts = report?.issueCounts ?? {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        Upgrade report: {clusterName}
        <IconButton
          aria-label="Close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!report ? (
          <Typography className={classes.emptyState}>
            No upgrade report is available for this cluster.
          </Typography>
        ) : (
          <>
            <Box className={classes.summary}>
              <Chip
                size="small"
                label={`Readiness: ${valueText(report.readiness)}`}
              />
              <Chip
                size="small"
                label={`${valueText(report.currentVersion)} -> ${valueText(
                  report.targetVersion,
                )}`}
              />
              <Chip size="small" label={`${steps.length} upgrade steps`} />
              <Chip
                size="small"
                label={`${count(issueCounts.critical)} critical / ${count(
                  issueCounts.warning,
                )} warning / ${count(issueCounts.info)} info`}
              />
              {report.generatedAt && (
                <Chip size="small" label={`Generated: ${report.generatedAt}`} />
              )}
            </Box>

            <Typography variant="h6" className={classes.sectionTitle}>
              Issues
            </Typography>
            {issues.length === 0 ? (
              <Typography className={classes.emptyState}>
                No issues recorded in the upgrade report.
              </Typography>
            ) : (
              <List disablePadding>
                {issues.map((issue, index) => (
                  <Box key={`${issue.resource ?? 'issue'}-${index}`}>
                    {index > 0 && <Divider />}
                    <ListItem className={classes.item}>
                      <ListItemText
                        primary={
                          <Box className={classes.actionRow}>
                            <Chip
                              size="small"
                              color={issueSeverityColor(
                                valueText(issue.severity, 'INFO'),
                              )}
                              label={valueText(issue.severity, 'INFO')}
                            />
                            <Typography variant="subtitle2">
                              {valueText(issue.category, 'Issue')}
                            </Typography>
                            {issue.resource && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={issue.resource}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {issue.description && (
                              <Typography
                                variant="body2"
                                className={classes.details}
                              >
                                {issue.description}
                              </Typography>
                            )}
                            {issue.remediation && (
                              <Typography
                                variant="body2"
                                className={classes.issueMeta}
                              >
                                Remediation: {issue.remediation}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}

            <Typography variant="h6" className={classes.sectionTitle}>
              Upgrade steps
            </Typography>
            {steps.length === 0 ? (
              <Typography className={classes.emptyState}>
                No upgrade steps recorded in the upgrade report.
              </Typography>
            ) : (
              <List disablePadding>
                {steps.map((step, index) => {
                  const order = valueText(step.order, String(index + 1));
                  const critical = Boolean(step.is_critical || step.isCritical);

                  return (
                    <Box key={`${order}-${index}`}>
                      {index > 0 && <Divider />}
                      <ListItem className={classes.item}>
                        <Box component="span" className={classes.order}>
                          {order}
                        </Box>
                        <ListItemText
                          primary={
                            <Box className={classes.actionRow}>
                              <Typography variant="subtitle2">
                                {valueText(step.action, 'Upgrade step')}
                              </Typography>
                              {critical && (
                                <Chip
                                  size="small"
                                  color="secondary"
                                  label="Critical"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            step.details ? (
                              <Typography
                                variant="body2"
                                className={classes.details}
                              >
                                {step.details}
                              </Typography>
                            ) : undefined
                          }
                        />
                      </ListItem>
                    </Box>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
